import ProductFormConfig from "./form";
import { ProductFormData, ProductRelatedAttributes } from "@/formTypes/ProductFormDataType";
import GenericForm from "../GenericForm";
import { ProductApi, ProductDataType } from "@/api/ProductsApi";
import { ProductFormDataKeys } from "@/generated/ProductFormDataKeys";
import { DynamicFormRef } from "@mavichpe/dynamic-forms";
import { useRef, useState } from "react";
import { CategoryApi } from "@/api/CategoryApi";
import { ProductlogApi } from "@/api/ProductlogApi";
import { ProductCostLogApi } from "@/api/ProductCostLogApi";
import { useAppSelector } from "@/store/hooks";
import { FORMULA_PRICES_MANAGEMENT_CODE } from "@utils/Constants";
import { roundCant } from "@utils/Cant";
import SearchCabysOffCanvas from "@components/layout/Offcanvas/SearchCabysOffCanvas";
import { EDOCUMENT_TEXT_MAX_LENGTH, sanitizeSafeText } from "@amplifyShared/safeText";

type ProductFormProps = {
  id?: string;
  copyData?: boolean
  formId: string,
  saveRedirectUrl: string
}

const ProductForm = (props: ProductFormProps) => {
  const { copyData = false } = props
  const formRef = useRef<DynamicFormRef>(null);
  const [unEditableFields, setUnEditableFields] = useState<Array<keyof ProductFormData>>([]);
  const { pricemanaged } = useAppSelector(state => state.suscription.suscription || {})

  const getProps = {
    selectionSet: [
      ...ProductFormDataKeys.filter(k => !ProductRelatedAttributes.includes(k)),
      "imagenAsset.*"
    ]
  }

  const onInputChange = async (name: keyof ProductFormData, value: any) => {
    if (name === "categoryId") {
      const formData = await formRef.current?.getFormData(false) as ProductFormData;
      formRef.current?.setFormValue("multiple", {}, "multipleData")
      const category = await CategoryApi.get(`${value}`);

      const fieldMapping: Record<string, keyof typeof category> = {
        codigo_cabys: "codigo_cabys",
        descripcion_fact: "nombre",
        tipo_producto: "tipo_producto",
        tipo_impuesto: "tipo_impuesto",
        porcentaje_impuesto: "porcentaje_impuesto",
        unidad_medida: "unidad_medida",
        exento: "exenta"
      };

      const letNewProductData: Record<string, any> = {};

      for (const [formKey, categoryKey] of Object.entries(fieldMapping)) {
        if (!(formData as any)[formKey]) {
          letNewProductData[formKey] = category[categoryKey];
        }
      }

      if (category.sin_inventario) {
        setUnEditableFields([
          ...unEditableFields,
          "existencias"
        ])
      } else {
        setUnEditableFields(
          unEditableFields.filter(f => f != "existencias")
        )
      }

      formRef.current?.setFormValue('multiple', letNewProductData, "multipleData");
    }

  }
  /**
   * `descripcion_fact` es la que termina en el nodo <Detalle> del XML de Hacienda y en el
   * payload que viaja al Step Function, asi que se normaliza y se recorta al limite del
   * XSD antes de guardarla: asi ningun producto puede quedar en la base con un caracter
   * que despues impida enviar la factura que lo incluya.
   */
  const sanitizeProductTexts = (data: ProductDataType): ProductDataType => {
    const sanitized: ProductDataType = {
      ...data,
      descripcion_fact: sanitizeSafeText(data.descripcion_fact, EDOCUMENT_TEXT_MAX_LENGTH),
    }
    if (data.descripcion !== undefined && data.descripcion !== null) {
      sanitized.descripcion = sanitizeSafeText(data.descripcion)
    }
    return sanitized
  }

  const createProduct = async (data: ProductDataType): Promise<ProductDataType> => {
    const product = await ProductApi.create(sanitizeProductTexts(data))
    const cant = Number(product.existencias || 0)
    ProductlogApi.createProductLog(0, cant, cant, `${product?.id}`, "Creacion de Producto")
    ProductCostLogApi.createProductCostLog("", `${product?.id}`, Number(product?.costo || 0))
    return product
  }

  const updateProduct = async (id: string, data: ProductDataType): Promise<ProductDataType> => {
    const existentProduct = await ProductApi.get(id);
    const savedProduct = await ProductApi.update(id, sanitizeProductTexts(data))
    const costoPrevio = Number(existentProduct?.costo || 0)
    const existenciasPrevias = Number(existentProduct?.existencias || 0)
    const existenciasActuales = Number(savedProduct?.existencias || 0)
    const costoActual = Number(savedProduct?.costo || 0)
    let amountChange = 0;
    if (existenciasPrevias != existenciasActuales) {
      amountChange = roundCant(existenciasActuales - existenciasPrevias);
      const productLogComment = "Ajuste de inventario hecho directamente en una modificacion de producto";
      ProductlogApi.createProductLog(existenciasPrevias, amountChange, existenciasActuales, `${savedProduct?.id}`, productLogComment)
    }
    if (costoPrevio != costoActual) {
      ProductCostLogApi.createProductCostLog("", `${savedProduct?.id}`, costoActual)
    }
    return savedProduct
  }

  const getFormConfig = () => {
    if (pricemanaged == FORMULA_PRICES_MANAGEMENT_CODE) {
      const price1Input = ProductFormConfig.sections.flatMap(s => s.fields).flat().find(f => f.name === "precio1")
      const price2Input = ProductFormConfig.sections.flatMap(s => s.fields).flat().find(f => f.name === "precio2")
      const price3Input = ProductFormConfig.sections.flatMap(s => s.fields).flat().find(f => f.name === "precio3")
      const price4Input = ProductFormConfig.sections.flatMap(s => s.fields).flat().find(f => f.name === "precio4")
      if (price1Input) {
        price1Input.type = "hidden"
      }
      if (price2Input) {
        price2Input.type = "hidden"
      }
      if (price3Input) {
        price3Input.type = "hidden"
      }
      if (price4Input) {
        price4Input.type = "hidden"
      }
    }
    return ProductFormConfig
  }

  const onCabysSelected = (product?: ProductDataType) => {
    formRef.current?.setFormValue("multipleData", product, "multipleData")
  }

  return (
    <>
      <GenericForm<ProductFormData, ProductDataType>
        id={props.id}
        ref={formRef}
        copyData={copyData}
        formId={props.formId}
        unEditableFields={unEditableFields}
        saveRedirectUrl={props.saveRedirectUrl}
        formConfig={getFormConfig()}
        modelRelativeObjects={ProductRelatedAttributes}
        getById={(...arg) => ProductApi.get(...arg, getProps)}
        createFn={(...arg) => createProduct(...arg)}
        updateFn={(...arg) => updateProduct(...arg)}
        onInputChange={onInputChange}
        successMessage="El Producto ha sido almacenada correctamente"
        errorMessage="No fue posible almacenar la información del Producto"
      />
      <SearchCabysOffCanvas
        onCabysSelected={onCabysSelected} />
    </>
  )
}

export default ProductForm;